// ─────────────────────────────────────────────────────────────────────────────
// Crew — Analytics SDK (SPINE: frozen event taxonomy + typed signatures).
// Owned by Agent D. Agent B calls track(). Phase 0 freezes ONLY the event names
// and call signatures so every other agent can wire events without waiting on D's
// pipeline. D fills in transport, batching, derived metrics, and /metrics later.
//
// The funnel IS the product's strategic core and fundraise artefact. The ORDER and
// NAMES below are the contract — do not rename, reorder, or add ad-hoc events.
// ─────────────────────────────────────────────────────────────────────────────

import type { ConsentScope } from './types';
import { getToken } from './api';
import { isBehaviouralEventAllowed, type ConsentSet } from './consent';

// ── The canonical funnel sequence ───────────────────────────────────────────
// group_created → member_joined → goal_created → goal_committed →
// time_locked → activity_viewed → intent_shortlisted → intent_committed →
// click_out / booking_reported → clo_verified_spend → group_retained (Dn)
export const FUNNEL_EVENTS = [
  'group_created',
  'member_joined',
  'goal_created',
  'goal_committed',
  'time_locked',
  'activity_viewed',
  'intent_shortlisted',
  'intent_committed',
  'click_out',
  'booking_reported',
  'clo_verified_spend',
  'group_retained',
] as const;

export type FunnelEvent = (typeof FUNNEL_EVENTS)[number];

// Events that are part of essential service operation always fire. Everything
// behavioural requires the `analytics` consent scope before it may be recorded.
export const ESSENTIAL_EVENTS: ReadonlySet<FunnelEvent> = new Set<FunnelEvent>([
  'group_created',
  'member_joined',
]);

export function requiresConsent(event: FunnelEvent): ConsentScope | null {
  return ESSENTIAL_EVENTS.has(event) ? null : 'analytics';
}

// ── Typed event property shapes ──────────────────────────────────────────────
// Minimal, additive. Every event carries groupId so funnels can be grouped by
// the proprietary unit of intent: the group.
export interface BaseEventProps {
  groupId: string;
  memberId?: string;
  occurredAt?: string; // ISO; defaults to send-time if omitted
}

export interface FunnelEventProps {
  group_created: BaseEventProps & { goalSeeded?: boolean };
  member_joined: BaseEventProps & { via: 'invite_code' | 'link' | 'starter' };
  goal_created: BaseEventProps & { goalId: string; goalType: string; seeded?: boolean };
  goal_committed: BaseEventProps & { goalId: string; participantCount: number };
  time_locked: BaseEventProps & { pollId: string; goalId?: string; slotId: string };
  activity_viewed: BaseEventProps & { activityId: string; offerType: string; cloEligible: boolean };
  intent_shortlisted: BaseEventProps & { intentId: string; activityId: string };
  intent_committed: BaseEventProps & { intentId: string; activityId: string; estimatedHeads: number };
  click_out: BaseEventProps & { intentId?: string; activityId: string; source: string };
  booking_reported: BaseEventProps & { intentId?: string; valuePence?: number; source: string };
  clo_verified_spend: BaseEventProps & { intentId?: string; valuePence: number; commissionPence?: number };
  group_retained: BaseEventProps & { dayN: number };
}

// ── The frozen call signature ────────────────────────────────────────────────
// Agent B imports and calls this. Agent D owns the implementation. In Phase 0 it
// is a typed no-op stub so the contract compiles and is callable everywhere.
export function track<E extends FunnelEvent>(event: E, props: FunnelEventProps[E]): void {
  // Consent-gated, batched transport (Agent D). Best-effort by design — the SERVER
  // is the consent authority (recordEvent re-checks against D1 and drops denied
  // behavioural events). Client-side gating is a courtesy: it avoids buffering /
  // sending events we already know will be dropped, but it never blocks essential
  // events and never throws into the calling UI.
  if (clientGateDenies(event)) return;

  buffer.push({ event, props });
  // First event arms the flush machinery (timer + lifecycle listeners). Doing it
  // lazily keeps this module side-effect-free on import.
  ensureArmed();

  if (buffer.length >= MAX_BATCH) flush();
  else scheduleFlush();
}

// ── Transport internals ──────────────────────────────────────────────────────
// Side-effect-free on import: nothing below runs until track() is first called.

interface BufferedEvent {
  event: FunnelEvent;
  props: FunnelEventProps[FunnelEvent];
}

const MAX_BATCH = 10; // flush once this many events accumulate
const FLUSH_INTERVAL_MS = 5_000; // …or after this idle window, whichever first
const MAX_BUFFER = 200; // hard cap so a token-less session never grows unbounded

const buffer: BufferedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let armed = false;

// Best-effort client consent snapshot. The client doesn't natively cache the
// consent set, so we lazily fetch it (once) and refresh it after consent changes.
// While unknown, we OPTIMISTICALLY buffer behavioural events and let the server
// gate decide — we never silently lose data the user actually consented to.
let consentSnapshot: ConsentSet | null = null;
let consentFetchInFlight = false;

// Returns true only when we are CONFIDENT the client should drop this event
// (we know consent and it forbids this event). Unknown consent → do not deny.
function clientGateDenies(event: FunnelEvent): boolean {
  ensureConsentSnapshot();
  if (consentSnapshot === null) return false; // unknown → defer to server gate
  return !isBehaviouralEventAllowed(event, consentSnapshot);
}

// Lazily fetch the consent set once. Import is avoided to dodge an api↔analytics
// runtime cycle for non-type values beyond getToken; we hit the endpoint directly.
function ensureConsentSnapshot(): void {
  if (consentSnapshot !== null || consentFetchInFlight) return;
  if (typeof fetch === 'undefined' || getToken() == null) return; // no session yet
  consentFetchInFlight = true;
  const base = (import.meta.env?.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  fetch(`${base}/api/consent`, { headers: { authorization: `Bearer ${getToken()}` } })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((data: unknown) => {
      consentSnapshot = (data as { consents?: ConsentSet }).consents ?? [];
    })
    .catch(() => {
      /* leave snapshot null → optimistic buffering, server stays authoritative */
    })
    .finally(() => {
      consentFetchInFlight = false;
    });
}

// Public hook for the UI to invalidate the snapshot after a consent change so the
// next event re-fetches the authoritative set. Additive — does not alter track().
export function refreshConsentSnapshot(): void {
  consentSnapshot = null;
  consentFetchInFlight = false;
}

function ensureArmed(): void {
  if (armed) return;
  armed = true;
  // Flush on page hide / tab switch so in-flight buffers aren't lost on navigation.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => flush());
  }
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  if (typeof setTimeout === 'undefined') return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

// Drain the buffer to POST /api/events. Degrades gracefully with no session
// token (events stay buffered, capped at MAX_BUFFER) and never throws.
function flush(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;

  const tok = getToken();
  if (tok == null || typeof fetch === 'undefined') {
    // No session yet — keep the most recent events for a later flush; drop oldest
    // beyond the cap so a long token-less session can't grow without bound.
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    return;
  }

  // Take a snapshot and clear the live buffer so new events accumulate cleanly.
  const batch = buffer.splice(0, buffer.length);
  const base = (import.meta.env?.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

  for (const item of batch) {
    const { groupId, occurredAt, ...rest } = item.props;
    const body = JSON.stringify({ event: item.event, groupId, occurredAt, props: rest });
    try {
      void fetch(`${base}/api/events`, {
        method: 'POST',
        keepalive: true, // survive the document being torn down (pagehide/visibility)
        headers: { 'content-type': 'application/json', authorization: `Bearer ${tok}` },
        body,
      }).catch(() => {
        /* best-effort: a failed analytics POST must never surface to the UI */
      });
    } catch {
      /* fetch construction failure (e.g. SSR) — swallow; analytics is non-critical */
    }
  }
}

// ── Derived metrics Agent D must compute later (documented here as contract) ──
// activation (group reaches first locked time), Wn/Mn retention cohorts,
// intent→conversion rate, GMV influenced, commission / take rate, CAC vs early LTV.
export type DerivedMetric =
  | 'activation_rate'
  | 'retention_wn'
  | 'retention_mn'
  | 'intent_to_conversion'
  | 'gmv_influenced'
  | 'take_rate'
  | 'cac_vs_early_ltv';
